export enum SecretScope {
  ACCOUNT = 'account',
  PROJECT = 'project'
}

export enum SecretType {
  VISIBLE = 'visible',      // Plaintext, always visible
  MASKED = 'masked',        // Encrypted, can be revealed with eye icon
  SENSITIVE = 'sensitive'   // Encrypted, never revealable (rotate only)
}

export enum SecretMode {
  LITE = 'lite',           // Single value for all environments
  POWER = 'power'          // Separate values per environment (base, dev, staging, prod)
}

export enum Environment {
  BASE = 'base',
  DEVELOPMENT = 'development',
  STAGING = 'staging',
  PRODUCTION = 'production'
}

// Core Secret interface matching database schema
export interface Secret {
  id: string;
  workspaceId: string;
  scope: SecretScope;
  projectId?: string;

  // Secret metadata
  key: string;
  type: SecretType;
  mode: SecretMode;

  // Encrypted values (structure depends on mode)
  // Lite mode: { base: "value" }
  // Power mode: { base: "val", development: "val", staging: "val", production: "val" }
  values: SecretValues | EncryptedValues;

  // Optional metadata
  description?: string;
  notes?: string;
  tags?: string[];

  // Audit fields
  createdBy: string;
  updatedBy?: string;
  createdAt: Date;
  updatedAt: Date;
}

// Decrypted secret values
export interface SecretValues {
  base?: string;
  development?: string;
  staging?: string;
  production?: string;
}

// Encrypted format (stored in database)
export interface EncryptedValues {
  encrypted: string;
  iv: string;
  authTag: string;
}

// DTOs for API operations
export interface CreateSecretDto {
  scope: SecretScope;
  projectId?: string;
  key: string;
  type: SecretType;
  mode: SecretMode;
  values: SecretValues;
  description?: string;
  notes?: string;
  tags?: string[];
}

export interface UpdateSecretDto {
  key?: string;
  type?: SecretType;
  mode?: SecretMode;
  values?: SecretValues;
  description?: string;
  notes?: string;
  tags?: string[];
}

export interface ImportEnvDto {
  scope: SecretScope;
  projectId?: string;
  envContent: string;  // .env file content
  mode?: SecretMode;   // Default to 'lite'
  type?: SecretType;   // Default to 'masked'
}

export interface LinkSecretsDto {
  projectId: string;
  secretIds: string[];
}

export interface UnlinkSecretDto {
  projectId: string;
  secretId: string;
}

export interface ToggleModeDto {
  mode: SecretMode;
  // When switching to power mode, optionally provide environment-specific values
  environmentValues?: SecretValues;
}

// Response types for frontend
export interface SecretResponse {
  id: string;
  workspaceId: string;
  scope: SecretScope;
  projectId?: string;
  key: string;
  type: SecretType;
  mode: SecretMode;
  values: SecretValues;  // Decrypted or masked based on type
  description?: string;
  notes?: string;
  tags?: string[];
  createdBy: string;
  updatedBy?: string;
  createdAt: Date;
  updatedAt: Date;
  // Link metadata (for project-scoped secrets)
  isLinked?: boolean;           // True if this is a linked account secret
  linkedFromAccountId?: string; // Source account secret ID if linked
  linkedProjects?: string[];    // For account secrets: list of projects using this secret
}

export interface EnvBudgetInfo {
  currentSize: number;  // In bytes
  warnLimit: number;    // 48KB
  hardLimit: number;    // 64KB
  isNearLimit: boolean;
  isAtLimit: boolean;
  percentage: number;
}

export interface SecretsListResponse {
  secrets: SecretResponse[];
  budget?: EnvBudgetInfo;
}

// Filter and query params
export interface SecretsQueryParams {
  scope: SecretScope;
  projectId?: string;
  type?: SecretType;
  mode?: SecretMode;
  search?: string;  // Search in key, description
  tags?: string[];
}

// Import results
export interface ImportEnvResult {
  success: boolean;
  created: number;
  updated: number;
  skipped: number;
  errors: string[];
}
